import { AxiosResponse } from 'axios';
import { debounce, isBoolean } from 'lodash-es';
import jwt_decode from 'jwt-decode';
import moment from 'moment';
import { User } from '../interface/user.interface';
import {
  makeSOPClient,
  makeSOPClientProtocol,
  SOPClientApi,
  TOKEN_DEADLINE
} from '@barracuda/sop-api-client';

const debug = process.env.REACT_APP_MODE === 'dev';

const VALIDATE_TOKEN_SECONDS = 10;
const ACTIVITY_TIMEOUT_DEBOUNCE_SECONDS = 30;
const ACTIVITY_TIMEOUT_MINUTES = 10;
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];

const REFRESH_TOKEN_MAX_RETRIES = 5;
const REFRESH_TOKEN_RETRY_DELAY_MS = 5_000;

export enum TokenRefreshErrorLevel {
  WARNING = 'warning',
  FATAL = 'fatal'
}

export interface TokenRefreshError {
  level: TokenRefreshErrorLevel;
  attempt: number;
  maxRetries: number;
  error: unknown;
}

type TokenRefreshErrorCallback = (event: TokenRefreshError) => void;

const tokenRefreshErrorListeners = new Set<TokenRefreshErrorCallback>();

let refreshInProgress = false;
let lastActiveAt = Date.now();

export const markUserPresence = async (e?: Event) => {
  lastActiveAt = Date.now();

  if (debug) {
    if (e?.target instanceof HTMLElement) {
      console.log(`Auth.markUserPresence(${e.type})`, e.target.nodeName);
    } else {
      console.log(`Auth.markUserPresence(${e?.type})`);
    }
  }
};

const markUserPresenceDebounced = debounce(
  markUserPresence,
  ACTIVITY_TIMEOUT_DEBOUNCE_SECONDS * 1000,
  { leading: true, maxWait: ACTIVITY_TIMEOUT_DEBOUNCE_SECONDS * 1000 }
);

ACTIVITY_EVENTS.forEach((eventName) => {
  document.addEventListener(eventName, markUserPresenceDebounced, {
    passive: true
  });
});

export const sopClient: SOPClientApi = makeSOPClient(
  makeSOPClientProtocol({
    baseUrl: process.env.REACT_APP_PESC_URL as string
  })
);

const dispatchLocalStorageEvent = (key: string, oldValue?: string, newValue?: string) => {
  window.dispatchEvent(
    new StorageEvent('storage', {
      storageArea: window.localStorage,
      key,
      oldValue,
      newValue
    })
  );
};

// at first run it is not yet stored in sop client
export const getSopToken = () => {
  return sopClient.session.getToken() || localStorage.getItem('token') || undefined;
};

export const removeSopToken = () => {
  setSopToken();
};

export const loadExistingSopToken = () => {
  setSopToken(getSopToken());
};

export const setSopToken = (token?: string) => {
  if (isTokenExpired(token)) {
    token = undefined;
  }

  if (debug) {
    console.log(`Auth.setSopToken(${token})`);
  }

  const oldToken = sopClient.session.getToken();
  if (oldToken === token) {
    if (!token) {
      sopClient.session.deleteToken();
      localStorage.removeItem('token');
    }

    return;
  }

  if (token) {
    sopClient.session.setToken(token);
    localStorage.setItem('token', token);
  } else {
    sopClient.session.deleteToken();
    localStorage.removeItem('token');
  }

  dispatchLocalStorageEvent('token', oldToken, token || undefined);
};

export interface GetOAuthUrlParams {
  verificationLicenseId?: number | null;
  ageConsentConfirmed?: boolean;
  termsAndPrivacyAccepted?: boolean;
  receiveMarketing?: boolean;
  desktop?: boolean;
}

export interface SignUpParams {
  email: string;
  password: string;
  captchaResult?: string;
  dateOfBirth?: string;
  countryOfResidenceId?: number;
  consents?: SignUpConsentsParams;
}

export interface AuthenticatedSignUpParams {
  stateToken: string;
  captchaResult?: string;
  dateOfBirth: string;
  countryOfResidenceId: number;
  consents: SignUpConsentsParams;
}

export type SignUpConsentsParams = {
  termsAndPrivacy: boolean;
  marketingAndOffers?: boolean;
  personalInformationCollectionAndUseForMarketing?: boolean;
  provisionOfPersonalInformationTo3rdParty?: boolean;
  overseasTransferOfPersonalInformation?: boolean;
  personalInformationCollection?: boolean;
  uniqueIdentificationInformationCollection?: boolean;
  sensitiveInformationCollection?: boolean;
};

export interface UserTokenInfo {
  token: string;
  user: User;
}

export const getOAuthUrl: (params: GetOAuthUrlParams) => string = ({
  verificationLicenseId,
  ageConsentConfirmed,
  termsAndPrivacyAccepted,
  receiveMarketing,
  desktop
}: GetOAuthUrlParams) => {
  const baseUrl = process.env.REACT_APP_PESC_URL;
  const redirectUrl = `${baseUrl}/api/authentication/oauth2/redirect/google`;
  const url = new URL(redirectUrl);
  const searchParams = new URLSearchParams();

  searchParams.append('url', `${baseUrl}/${desktop ? 'desktop-callback' : 'callback'}`);

  const searchParamsFlags: Record<string, boolean | undefined> = {
    ageConsentConfirmed,
    termsAndPrivacyAccepted,
    receiveMarketing
  };
  Object.entries(searchParamsFlags).forEach(([key, value]) => {
    if (isBoolean(value)) {
      searchParams.append(key, value.toString());
    }
  });
  searchParams.append('applicationCode', 'ODYSSEY');
  if (verificationLicenseId) {
    searchParams.append('verificationLicenseId', verificationLicenseId.toString());
  }
  url.search = searchParams.toString();

  return url.toString();
};

export const signUp = async ({
  email,
  password,
  captchaResult,
  dateOfBirth,
  countryOfResidenceId,
  consents
}: SignUpParams) => {
  const response = await sopClient.signUp({
    email,
    password,
    captchaResult,
    consents,
    dateOfBirth,
    countryOfResidenceId
  });

  if (response.status === 409) {
    return Promise.reject(response);
  }

  return response as AxiosResponse;
};

export const completeSSOSignUp = async ({
  stateToken,
  captchaResult,
  dateOfBirth,
  countryOfResidenceId,
  consents
}: AuthenticatedSignUpParams) => {
  const response = await sopClient.completeSSOSignUp({
    stateToken,
    captchaResult,
    dateOfBirth,
    countryOfResidenceId,
    consents
  });

  if (response.data?.token) {
    setSopToken(response.data.token);
  }

  return response as AxiosResponse<UserTokenInfo>;
};

export const signIn = async (
  email: string,
  password: string,
  ignoreSavingToken?: boolean,
  captchaResult?: string
) => {
  markUserPresence();

  const response = await sopClient.signIn(email, password, captchaResult);
  if (response.data.token && !ignoreSavingToken) {
    setSopToken(response.data.token);
  }

  return response;
};

export const logout = async (loggedBefore?: string) => {
  await addLoggedBefore(loggedBefore);
  await sopClient.logout();
  removeSopToken();
  localStorage.removeItem('recentOrder');
};

export const addLoggedBefore = async (loggedBefore?: string) => {
  const payload = {
    loggedBefore: 'true'
  };

  if (!loggedBefore) {
    await sopClient.addMetadataToProfile(payload);
  }
};

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const notifyTokenRefreshError = (event: TokenRefreshError) => {
  tokenRefreshErrorListeners.forEach((cb) => cb(event));
};

const refreshToken = async () => {
  for (let attempt = 1; attempt <= REFRESH_TOKEN_MAX_RETRIES; attempt++) {
    try {
      const response = await sopClient.refreshToken();
      const token = response?.data?.token;

      setSopToken(token);

      return;
    } catch (error) {
      console.log(`Token refresh attempt ${attempt}/${REFRESH_TOKEN_MAX_RETRIES} failed`, error);

      if (attempt < REFRESH_TOKEN_MAX_RETRIES) {
        notifyTokenRefreshError({
          level: TokenRefreshErrorLevel.WARNING,
          attempt,
          maxRetries: REFRESH_TOKEN_MAX_RETRIES,
          error
        });
        await delay(REFRESH_TOKEN_RETRY_DELAY_MS);
      } else {
        notifyTokenRefreshError({
          level: TokenRefreshErrorLevel.FATAL,
          attempt,
          maxRetries: REFRESH_TOKEN_MAX_RETRIES,
          error
        });
        removeSopToken();
      }
    }
  }
};

export const isTokenCloseToExpired = (token: string | undefined): boolean => {
  const timeLeft = getTokenExpirationTime(token);

  return timeLeft < TOKEN_DEADLINE;
};

export const isTokenExpired = (token: string | null | undefined) => {
  if (token) {
    try {
      return getTokenExpirationTime(token) <= 0;
    } catch (e) {
      console.log('Token is invalid', e);
    }
  }

  return true;
};

export const getTokenExpirationTime = (token: string | undefined): number => {
  if (token) {
    try {
      const decodedToken: { exp: number } = jwt_decode(token);

      return moment(moment(decodedToken?.exp * 1000)).diff(Date.now(), 'seconds');
    } catch (e) {
      console.log('Token is invalid', e);
    }
  }

  return 0;
};

let sopTokenCheckTimeout: NodeJS.Timer | undefined;

const checkSopToken = () => {
  clearTimeout(sopTokenCheckTimeout);
  sopTokenCheckTimeout = setTimeout(() => checkSopToken(), VALIDATE_TOKEN_SECONDS * 1000);

  const sopToken = getSopToken();
  if (sopToken) {
    if (isTokenExpired(sopToken)) {
      removeSopToken();

      return;
    }

    if (!refreshInProgress && isTokenCloseToExpired(sopToken)) {
      const now = Date.now();
      const howManyMinutesAgoWasActive = (now - lastActiveAt) / (1000 * 60);
      if (howManyMinutesAgoWasActive < ACTIVITY_TIMEOUT_MINUTES) {
        refreshInProgress = true;

        refreshToken().finally(() => (refreshInProgress = false));
      }
    }
  }
};

export const addTokenRefreshErrorListener = (callback: TokenRefreshErrorCallback): (() => void) => {
  tokenRefreshErrorListeners.add(callback);

  return () => {
    tokenRefreshErrorListeners.delete(callback);
  };
};

export const addTokenUpdatedListener = (callback: (token?: string) => void): (() => void) => {
  let listener = (e: StorageEvent) => {
    if (e.storageArea === window.localStorage && e.key === 'token') {
      if (e.oldValue !== e.newValue) {
        const token = e.newValue ? e.newValue : undefined;
        if (debug) {
          console.log('Auth.tokenUpdatedEvent', token, e);
        }

        sopClient.session.setToken(token as string);

        callback(token);
      }
    }
  };

  window.addEventListener('storage', listener);
  checkSopToken();

  return () => {
    window.removeEventListener('storage', listener);
    clearTimeout(sopTokenCheckTimeout);
  };
};
